import { Component, inject, OnInit, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FirmaService } from '../../../../shared/services/firma.service';

@Component({
	selector: 'app-firma-otp-gate',
	standalone: true,
	imports: [CommonModule, FormsModule],
	templateUrl: './firma-otp-gate.component.html',
})
export class FirmaOtpGateComponent implements OnInit {
	private route = inject(ActivatedRoute);
	private router = inject(Router);
	private firmaService = inject(FirmaService);

	idTransaction = signal('');

	// Stato
	loading = signal(true);
	loadingAction = signal(false);
	errorMsg = signal('');

	// Dati dalla API
	operationToken = signal('');
	operationID = signal('');
	phone = signal('');
	praticaData = signal<any>(null);
	authFirst = signal(false);

	// Cifre OTP
	otpDigits = signal<string[]>(['', '', '', '', '', '']);
	timerValue = signal(300);

	isOtpComplete = computed(() => this.otpDigits().every((d) => d.length === 1));
	otpCode = computed(() => this.otpDigits().join(''));
	timerDisplay = computed(() => {
		const m = Math.floor(this.timerValue() / 60);
		const s = this.timerValue() % 60;
		return `${m}:${s < 10 ? '0' : ''}${s}`;
	});

	private timerInterval: any;

	ngOnInit() {
		const id = this.route.snapshot.paramMap.get('idTransaction') ?? '';
		this.idTransaction.set(id);

		if (!id) {
			this.errorMsg.set('Link non valido.');
			this.loading.set(false);
			return;
		}

		this.firmaService.getPraticaDetail(id).subscribe({
			next: (statusRes: any) => {
				const token = statusRes.operationToken;
				this.operationToken.set(token);
				this.praticaData.set(statusRes);
				this.phone.set(statusRes.phone || '');
				this.authFirst.set(statusRes.authFirst ?? true);

				this.firmaService.startAuthOperation(id, token).subscribe({
					next: (authRes: any) => {
						const opId = authRes.operationID;
						this.operationID.set(opId);
						this.loading.set(false);

						if (!statusRes.authFirst) {
							// Nessun OTP richiesto, naviga direttamente
							this.openAndNavigate(0, opId, token);
						} else {
							this.startTimer();
						}
					},
					error: () => {
						this.loading.set(false);
						this.errorMsg.set("Impossibile avviare l'operazione di firma.");
					},
				});
			},
			error: () => {
				this.loading.set(false);
				this.errorMsg.set('Link non valido o pratica non disponibile.');
			},
		});
	}

	startTimer() {
		if (this.timerInterval) clearInterval(this.timerInterval);
		this.timerInterval = setInterval(() => {
			this.timerValue.update((v) => {
				if (v <= 0) {
					clearInterval(this.timerInterval);
					return 0;
				}
				return v - 1;
			});
		}, 1000);
	}

	resendOtp() {
		if (this.timerValue() > 0) return;

		this.loadingAction.set(true);
		this.errorMsg.set('');

		this.firmaService.startAuthOperation(this.idTransaction(), this.operationToken()).subscribe({
			next: (authRes: any) => {
				this.operationID.set(authRes.operationID);
				this.timerValue.set(300);
				this.startTimer();
				this.otpDigits.set(['', '', '', '', '', '']);
				this.loadingAction.set(false);
			},
			error: () => {
				this.loadingAction.set(false);
				this.errorMsg.set("Impossibile ricaricare il codice OTP.");
			},
		});
	}

	conferma() {
		if (!this.isOtpComplete()) return;
		const opId = this.operationID();
		const token = this.operationToken();
		this.openAndNavigate(parseInt(this.otpCode(), 10), opId, token);
	}

	openAndNavigate(otp: number, opId: string, token: string) {
		const id = this.idTransaction();
		this.loadingAction.set(true);
		this.errorMsg.set('');

		this.firmaService.openTransaction(id, token, otp, opId).subscribe({
			next: () => {
				this.loadingAction.set(false);
				clearInterval(this.timerInterval);
				this.firmaService.setAuthenticated(id, opId);
				this.router.navigate(['/web/public/firma-otp/dettaglio', id], {
					state: {
						pratica: this.praticaData(),
						operationID: opId,
						authenticated: true,
					},
				});
			},
			error: () => {
				this.loadingAction.set(false);
				this.errorMsg.set('Codice OTP non valido. Riprova.');
			},
		});
	}

	onOtpInput(index: number, event: Event) {
		const input = event.target as HTMLInputElement;
		const val = input.value.replace(/\D/g, '');
		const digit = val.slice(-1);

		this.otpDigits.update((digits) => {
			const newDigits = [...digits];
			newDigits[index] = digit;
			return newDigits;
		});

		input.value = digit;

		if (digit && index < 5) {
			const next = document.getElementById(`otp-gate-${index + 1}`) as HTMLInputElement;
			next?.focus();
		}
	}

	onOtpKeydown(index: number, event: KeyboardEvent) {
		if (event.key === 'Backspace' && !this.otpDigits()[index] && index > 0) {
			const prev = document.getElementById(`otp-gate-${index - 1}`) as HTMLInputElement;
			prev?.focus();
		}
	}

	onOtpPaste(event: ClipboardEvent) {
		const pastedText = event.clipboardData?.getData('text');
		if (!pastedText) return;

		const digits = pastedText.replace(/\D/g, '').slice(0, 6).split('');
		if (digits.length === 0) return;

		event.preventDefault();

		this.otpDigits.update(() => {
			const newDigits = ['', '', '', '', '', ''];
			for (let i = 0; i < digits.length; i++) {
				newDigits[i] = digits[i];
			}
			return newDigits;
		});

		const nextIndex = Math.min(digits.length, 5);
		setTimeout(() => {
			const next = document.getElementById(`otp-gate-${nextIndex}`) as HTMLInputElement;
			next?.focus();
		}, 0);
	}

	reload() {
		window.location.reload();
	}
}
